export type Theme = 'light' | 'dark' | 'system'

const STORAGE_KEY = 'cda-theme'

/**
 * Applies the theme to the document root.
 *
 * "system" removes the attribute entirely rather than resolving it to a value,
 * so the CSS media query stays in charge and the page follows the OS if the
 * user changes it while the tab is open.
 */
export function applyTheme(theme: Theme): void {
  const root = document.documentElement
  if (theme === 'system') root.removeAttribute('data-theme')
  else root.setAttribute('data-theme', theme)
}

export function loadTheme(): Theme {
  const stored = localStorage.getItem(STORAGE_KEY)
  return stored === 'light' || stored === 'dark' ? stored : 'system'
}

export function saveTheme(theme: Theme): void {
  if (theme === 'system') localStorage.removeItem(STORAGE_KEY)
  else localStorage.setItem(STORAGE_KEY, theme)
}

/** What the user is actually looking at right now. */
export function resolvedTheme(theme: Theme): 'light' | 'dark' {
  if (theme !== 'system') return theme
  return window.matchMedia('(prefers-color-scheme: dark)').matches ? 'dark' : 'light'
}

/**
 * Applies the stored theme before React mounts.
 *
 * Called from main.tsx rather than inside a component: waiting for an effect
 * means the page paints in the default theme first and then flips, which is
 * the flash everyone notices.
 */
export function initTheme(): void {
  applyTheme(loadTheme())
}
